import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { AssistantMessage } from '@assistant-ui/react-ui';
import { useAuiState, useMessagePartText } from '@assistant-ui/react';
import { speakText, stopSpeaking, useReadAloudEnabled } from '../../voice/read-aloud';
import { AlertTriangle } from 'lucide-react';
import { AgentChainOfThought } from './agent-chain-of-thought';
import { useRunElapsedSeconds, ThoughtHeaderContent, COT_HEADER_BASE_CLASS } from './cot-header';
import { MarkdownText } from './markdown-text';
import { SurfaceLinkBadges } from './SurfaceLinkBadges';
import { DelegationLinkChip } from './DelegationLinkChip';
import { AssistantResponseActionBar } from './agent-assistant-action-bar';
import { runStateStore } from '../../hooks/useAsyncInvokeAdapter';
import type { InlineAskUserArgs } from './inline-ask-user-card';
import { AskUserRegistration } from './pending-ask-user';
import { AskUserSummary } from './ask-user-summary';
import { RunPausedNotice } from './run-paused-notice';
import type { AskUserQuestion, AskUserRef } from '@/types/inbox';

/** Suppresses the running dot during CoT; shows it once text content is streaming. */
function AgentEmptyText({ status }: { status: { type: string } }) {
  const messageMetadata = useAuiState((s) => s.message.metadata);
  const runId = (messageMetadata?.custom as Record<string, unknown> | undefined)?.runId as string | undefined;
  const subscribe = useCallback(
    (listener: () => void) => runStateStore.subscribe(runId, listener),
    [runId],
  );
  const getSnapshot = useCallback(
    () => runStateStore.getSnapshot(runId),
    [runId],
  );
  const snapshot = useSyncExternalStore(subscribe, getSnapshot);
  if (status.type === 'running' && !snapshot.progress.hasTextContent) return null;
  // Return null — the Empty component is only rendered when there are no text parts.
  // MarkdownText requires a part scope provider which isn't available here.
  return null;
}

/**
 * Wraps MarkdownText but renders error messages (text starting with "Error: "
 * on an incomplete message) in a styled alert box with a retry hint.
 */
function ErrorAwareText(props: Record<string, unknown>) {
  const messageStatus = useAuiState((s) => s.message.status);
  const messageMetadata = useAuiState((s) => s.message.metadata);
  // The Text override receives no part prop in this assistant-ui version —
  // the part lives in context and must be read via useMessagePartText.
  const { text } = useMessagePartText();
  const errorCode = (messageMetadata?.custom as Record<string, unknown> | undefined)
    ?.errorCode as string | undefined;

  const isError =
    messageStatus?.type === 'incomplete' &&
    (messageStatus as { reason?: string }).reason === 'error' &&
    text.startsWith('Error: ');

  if (isError) {
    // LEGACY ONLY. Runs that exhaust their iteration budget now terminate in
    // the non-error `incomplete` status and render via `RunPausedNotice` — this
    // branch survives for runs persisted as `error`/MAX_ITERATIONS before that
    // change, which restore from history with the old shape.
    if (errorCode === 'MAX_ITERATIONS') {
      return (
        <div className="my-2 flex gap-2 rounded-md border border-amber-500/20 bg-amber-500/10 p-3 text-sm text-amber-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="min-w-0">
            <p className="font-medium">Paused before finishing</p>
            <p className="mt-1 text-amber-300/90">
              The agent ran out of working steps before finishing. Everything it
              completed is saved — reply to ask it to continue, or raise the
              agent&apos;s max iterations setting.
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="my-2 flex gap-2 rounded-md border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0">
          <p>{text}</p>
          <p className="mt-1 text-xs text-red-400/70">Click ↻ to retry</p>
        </div>
      </div>
    );
  }

  return <MarkdownText {...props} />;
}

/**
 * Fallback "Thought for Xs" header for single-turn answers. assistant-ui only
 * mounts the ChainOfThought slot when the message has reasoning/tool-call parts,
 * so a one-turn reply (no tools, no reasoning) would otherwise render with no
 * header at all. We render the collapsed header from the moment the answer
 * starts streaming (see the caller) so every answer carries the same timing
 * chrome without it blinking in late.
 */
function SingleTurnThoughtHeader() {
  // Freeze immediately to the elapsed-since-run-start (via the store's stamped
  // `startedAt`, not this component's mount) — i.e. the time-to-first-token,
  // matching how the ChainOfThought header freezes when the answer begins.
  const elapsed = useRunElapsedSeconds(false);
  return (
    <div className="mt-0.5 mb-2 overflow-hidden">
      <div className={`${COT_HEADER_BASE_CLASS} cursor-default`}>
        <ThoughtHeaderContent working={false} label="" elapsedSeconds={elapsed} />
      </div>
    </div>
  );
}

/**
 * Read-aloud ("typed input, spoken replies"): when enabled, speak the answer
 * text the moment this message transitions running → complete. Transition
 * tracking (not plain status) means restored history — which mounts already
 * complete — is never replayed aloud, and StrictMode's double mount sees no
 * transition either. A new run starting cancels any speech still playing
 * (text barge-in).
 */
function ReadAloudOnComplete() {
  const enabled = useReadAloudEnabled();
  const status = useAuiState((s) => s.message.status);
  const parts = useAuiState((s) => s.message.parts);
  const prevStatusRef = useRef<string | undefined>(status?.type);

  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = status?.type;
    if (!enabled) return;
    if (status?.type === 'running' && prev !== 'running') stopSpeaking();
    if (status?.type === 'complete' && prev === 'running') {
      const text = parts
        .filter((p) => p.type === 'text')
        .map((p) => (p as { text?: string }).text ?? '')
        .join('\n\n');
      if (text.trim()) speakText(text);
    }
  }, [status?.type, enabled, parts]);

  return null;
}

/**
 * Wraps all column-2 children (content + chips + action bar) in a single grid
 * cell with normal flow layout. No vertical space reservation here: the
 * viewport pins the user message to the top and reserves a full screen of
 * scrollable space below it (see `AnchoredViewport` in conversation-thread),
 * so the CoT growing, collapsing to "Thought for Xs", and the final answer
 * rendering all reflow *below* the pinned message without moving the scroll
 * position.
 */
function MessageColumn() {
  const messageStatus = useAuiState((s) => s.message.status);
  const parts = useAuiState((s) => s.message.parts);
  const messageMetadata = useAuiState((s) => s.message.metadata);

  // assistant-ui renders the ChainOfThought slot (which owns the header) only
  // when reasoning/tool-call parts exist. For a successful single-turn answer
  // there are none, so we synthesize the header ourselves. Show it as soon as
  // the answer text starts streaming (not only on complete): a no-reasoning
  // single-turn answer drops its reasoning placeholder the instant text
  // arrives, so the ChainOfThought slot unmounts mid-run — without this the
  // header would blink out during streaming and only reappear once complete.
  const hasCotParts = parts.some((p) => p.type === 'reasoning' || p.type === 'tool-call');
  const hasAnswerText = parts.some(
    (p) => p.type === 'text' && !!(p as { text?: string }).text,
  );
  // `complete` covers the settled answer; `running && hasAnswerText` covers the
  // streaming phase. Deliberately excludes `incomplete` (error / cancelled) so
  // an error alert doesn't get a "Thought for Xs" header above it.
  const showFallbackHeader =
    !hasCotParts &&
    (messageStatus?.type === 'complete' ||
      (messageStatus?.type === 'running' && hasAnswerText));

  // Ask-user (HITL) questions are synthesized as `__ask_user` tool-call parts.
  // The message doesn't render them inline — it registers them so the pending
  // question shows as an interactive card attached above the composer
  // (`ComposerAskUserPanel`). Excluded from the chain-of-thought in `cot-derive`.
  const askUserParts = parts.filter(
    (p) =>
      p.type === 'tool-call' &&
      (p as { toolName?: string }).toolName === '__ask_user',
  ) as Array<{ toolCallId?: string; args?: InlineAskUserArgs }>;

  // Question summaries shown inline so the asking turn isn't a blank bubble.
  // Prefer the persisted `askUserRef` snapshot (covers reload, where no live
  // `__ask_user` part exists); fall back to the synthesized part during a run.
  // Dedup by request id so a turn carrying both sources renders once.
  const askUserRef = (
    messageMetadata?.custom as { askUserRef?: AskUserRef } | undefined
  )?.askUserRef;
  const questionSummaries: Array<{ id: string; questions: AskUserQuestion[] }> = [];
  const summarized = new Set<string>();
  if (askUserRef?.kind === 'question' && askUserRef.snapshot.questions.length) {
    questionSummaries.push({
      id: askUserRef.askUserRequestId,
      questions: askUserRef.snapshot.questions,
    });
    summarized.add(askUserRef.askUserRequestId);
  }
  askUserParts.forEach((p, i) => {
    const id = p.args?.askUserRequestId ?? '';
    if (id && summarized.has(id)) return;
    if (p.args?.questions?.length) {
      questionSummaries.push({ id: id || `part-${i}`, questions: p.args.questions });
      if (id) summarized.add(id);
    }
  });

  return (
    <div className="col-start-1 col-end-[-1] row-start-1">
      <ReadAloudOnComplete />
      {showFallbackHeader && <SingleTurnThoughtHeader />}
      <AssistantMessage.Content
        components={{
          ChainOfThought: AgentChainOfThought,
          Text: ErrorAwareText,
          Empty: AgentEmptyText,
        }}
      />
      {questionSummaries.map((s) => (
        <AskUserSummary key={s.id} questions={s.questions} />
      ))}
      {askUserParts.map((p, i) => (
        <AskUserRegistration
          key={p.toolCallId ?? p.args?.askUserRequestId ?? i}
          args={p.args ?? {}}
        />
      ))}
      <RunPausedNotice />
      <SurfaceLinkBadges />
      <DelegationLinkChip />
      <AssistantResponseActionBar />
    </div>
  );
}

/**
 * Custom AssistantMessage that uses ChainOfThought to group all reasoning
 * and tool-call parts into a Grok-style collapsible thinking section.
 * Text parts render normally below as the final response.
 */
export function AgentAssistantMessage() {
  return (
    <AssistantMessage.Root>
      <MessageColumn />
    </AssistantMessage.Root>
  );
}
